import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";

/**
 * EditEntityDialog — single shared edit form for transactions and invoices.
 *
 * Used by DocumentCompareDrawer when an `editTarget` is supplied. Loads the
 * current entity values via the appropriate `getById` query, lets the user
 * patch the editable fields, and submits via `transactions.update` or
 * `invoices.update`.
 *
 * Invoice rule: when an invoice is saved here it counts as human-validated, so
 * we force `ocrConfidence = 100` and `extractionStatus = "extracted"`. This
 * unblocks the matching pipeline for invoices that were stuck in `failed`
 * extraction (the typical "Walmart vendor portal 40% failed unmatched" case).
 */

export type EditTarget =
  | { type: "transaction"; id: string }
  | { type: "invoice"; id: string };

interface Props {
  open: boolean;
  onClose: () => void;
  target: EditTarget | null;
  /** Called after a successful save (parent can close the parent drawer etc.). */
  onSaved?: () => void;
}

export function EditEntityDialog({ open, onClose, target, onSaved }: Props) {
  // Form state — kept as strings so empty inputs don't break number parsing.
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [reference, setReference] = useState("");
  const [category, setCategory] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Load current values once the dialog opens. We use `useQuery` so the form
  // reflects the latest server state, including any concurrent edits made
  // elsewhere in the app.
  const txn = useQuery(
    api.transactions.getById,
    open && target?.type === "transaction" ? { id: target.id as any } : "skip",
  );
  const invoice = useQuery(
    api.invoices.getById,
    open && target?.type === "invoice" ? { id: target.id as any } : "skip",
  );

  const updateTransaction = useMutation(api.transactions.update);
  const updateInvoice = useMutation(api.invoices.update);

  // Hydrate the form once the entity arrives. The dependency on the entity's
  // _id covers the "switched to a different row while the dialog stayed open"
  // case (we don't actually do that, but it's safer than depending on the
  // identity of the object).
  useEffect(() => {
    if (!open) return;
    if (target?.type === "transaction" && txn) {
      setDate(txn.date ?? "");
      setAmount(txn.amount != null ? String(txn.amount) : "");
      setDescription(txn.description ?? "");
      setVendorName(txn.vendorName ?? "");
      setReference(txn.reference ?? "");
      setCategory(txn.category ?? "");
    } else if (target?.type === "invoice" && invoice) {
      setDate(invoice.date ?? "");
      setAmount(invoice.amount != null ? String(invoice.amount) : "");
      setVendorName(invoice.vendorName ?? "");
      setInvoiceNumber(invoice.invoiceNumber ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, txn?._id, invoice?._id]);

  const reset = () => {
    setDate("");
    setAmount("");
    setDescription("");
    setVendorName("");
    setReference("");
    setCategory("");
    setInvoiceNumber("");
    setErrorMsg(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const parseAmount = (raw: string): number | null => {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    return n;
  };

  const handleSave = async () => {
    if (!target) return;
    setErrorMsg(null);

    const parsedAmount = parseAmount(amount);
    if (amount !== "" && parsedAmount === null) {
      setErrorMsg("Amount must be a valid number.");
      return;
    }

    setSaving(true);
    try {
      if (target.type === "transaction") {
        await updateTransaction({
          id: target.id as any,
          date: date || undefined,
          amount: parsedAmount ?? undefined,
          description: description || undefined,
          vendorName: vendorName || undefined,
          reference: reference || undefined,
          category: category || undefined,
        });
        setSnackbar({
          open: true,
          message: "Transaction updated.",
          severity: "success",
        });
      } else {
        // Invoice: human validated → force confidence to 100 and status to
        // extracted so the matching pipeline picks the row up on the next pass.
        await updateInvoice({
          id: target.id as any,
          vendorName: vendorName || undefined,
          invoiceNumber: invoiceNumber || undefined,
          date: date || undefined,
          amount: parsedAmount ?? undefined,
          ocrConfidence: 100,
          extractionStatus: "extracted",
        });
        setSnackbar({
          open: true,
          message:
            "Invoice updated. Confidence set to 100% — matching will pick it up shortly.",
          severity: "success",
        });
      }
      onSaved?.();
      reset();
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message ?? "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const loading =
    target?.type === "transaction"
      ? txn === undefined
      : target?.type === "invoice"
        ? invoice === undefined
        : false;

  const titleText =
    target?.type === "transaction"
      ? "Edit Transaction"
      : target?.type === "invoice"
        ? "Edit Invoice"
        : "Edit";

  return (
    <>
      <Dialog
        open={open && Boolean(target)}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {titleText}
          </Typography>
          {target?.type === "invoice" && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Saving will mark this invoice as human-validated (confidence 100%)
              and put it back into the matching queue.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

              <TextField
                label="Vendor"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                inputProps={{ step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              {target?.type === "transaction" && (
                <>
                  <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <TextField
                    label="Reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                  />
                </>
              )}

              {target?.type === "invoice" && (
                <TextField
                  label="Invoice #"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
